// Libraries
import { select, selectAll, nest } from 'd3';
import scrollama from 'scrollama';
import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  Shape,
  ShapeGeometry,
  Mesh,
  MeshPhongMaterial,
  BoxGeometry,
  MeshBasicMaterial,
  DoubleSide,
  BoxHelper,
  TextureLoader,
  SRGBColorSpace,
  RepeatWrapping,
  Color,
  Texture,
  Vector3,
  Clock,
} from 'three';
import gsap from 'gsap';

// Internal functions
import createFishShape1 from './shapes/fishShape1';
import createFishShape2 from './shapes/fishShape2';
import createFishShape3 from './shapes/fishShape3';
import createFishShape4 from './shapes/fishShape4';

// Data
import context from '../../data/text.json';
import data from '../../data/data.json';

/**
 * Setup
 */
const text = context['scrolly_1'];
const miniResultsData = data['HD142_mini-results'];
const positionsData = data['HD142_position'];
const scrollyData = data['HD142_scrolly'];
const annotationsData = data['scrolly_overlay__'];

const scroller = scrollama();

/**
 * Parameters
 */
const scrolly = 1;
const colors = {
  red: '#d64946',
  blue: '#21789B',
  backgroudNavy: '#000016',
  yellow: '#be9304',
  gray: '#686868',
  white: '#eeeeee',
};
const rem = 16;
let view = 'mobile';

const params = {
  desktop: {
    aspectRatio: 5 / 9,
    meshMarginScale: { x: 12, y: 10 },
    meshScale: 0.3,
    meshTranslateScale: { x: 24, y: 20 },
    meshRotationScale: 2,
    meshRotationDuration: 5,
    meshStaggerScale: 0.0025,
    cameraPosition: 150,
  },
  mobile: {
    aspectRatio: 9 / 5,
    meshMarginScale: { x: 12, y: 10 },
    meshScale: 0.3,
    meshTranslateScale: { x: 24, y: 60 },
    meshRotationScale: 2,
    meshRotationDuration: 5,
    meshStaggerScale: 0.0025,
    cameraPosition: 600,
  },
};

const breakpoints = [
  { cat: 'xl', width: 1600, defaultHeight: 1600 * 0.33 },
  { cat: 'l', width: 1140, defaultHeight: 1140 * 0.33 },
  { cat: 'm', width: 1000, defaultHeight: 1000 * 0.33 },
  { cat: 't', width: 800, defaultHeight: 800 * 0.31 },
  { cat: 'r', width: 664, defaultHeight: 664 * params[view].aspectRatio },
  { cat: 's', width: 540, defaultHeight: 540 * params[view].aspectRatio },
  { cat: 'xs', width: 370, defaultHeight: 370 * params[view].aspectRatio },
  { cat: 'xxs', width: 320, defaultHeight: 320 * params[view].aspectRatio },
];

/**
 * Could move to utils?
 */
const getWindowSize = () => {
  const width = window.innerWidth;
  const height = window.innerHeight;

  return { width, height };
};

// const getFigureSize = (windowSize) => {
//   const figureSize = {};

//   if (windowSize.width > 1600) {
//     figureSize.width = Math.floor(1600 * 0.6);
//   } else if (windowSize.width > 800) {
//     figureSize.width = Math.floor(windowSize.width * 0.6);
//   } else {
//     figureSize.width = Math.floor(windowSize.width - 4 * rem);
//   }
//   figureSize.height = Math.floor(figureSize.width * params[view].aspectRatio);

//   return (figureSize);
// }

const getFigureSize = windowSize => {
  const figureSize = {};
  let index;

  if (windowSize.width > breakpoints[0].width) {
    figureSize.width = breakpoints[0].width * 0.7;
    index = 0;
    figureSize.breakpoint = breakpoints[0].width;
  } else if (windowSize.width > breakpoints[1].width) {
    figureSize.width = breakpoints[1].width * 0.7;
    index = 1;
    figureSize.breakpoint = breakpoints[1].width;
  } else if (windowSize.width > breakpoints[2].width) {
    figureSize.width = breakpoints[2].width * 0.6;
    index = 2;
    figureSize.breakpoint = breakpoints[2].width;
  } else if (windowSize.width > breakpoints[3].width) {
    figureSize.width = breakpoints[3].width * 0.6;
    index = 3;
    figureSize.breakpoint = breakpoints[3].width;
  } else if (windowSize.width > breakpoints[4].width) {
    figureSize.width = breakpoints[4].width;
    index = 4;
    figureSize.breakpoint = breakpoints[4].width;
  } else if (windowSize.width > breakpoints[5].width) {
    figureSize.width = breakpoints[5].width;
    index = 5;
    figureSize.breakpoint = breakpoints[5].width;
  } else if (windowSize.width > breakpoints[6].width) {
    figureSize.width = breakpoints[6].width;
    index = 6;
    figureSize.breakpoint = breakpoints[6].width;
  } else {
    figureSize.width = 320;
    index = 7;
    figureSize.breakpoint = breakpoints[7].width;
  }

  figureSize.height = breakpoints[index].defaultHeight;

  // if (figureSize.height > windowSize.height) {
  //   if (view = "desktop") {
  //     figureSize.height = figureSize.height <= breakpoints[1].defaultHeight
  //       ? breakpoints[1].defaultHeight
  //       : figureSize.height <= breakpoints[2].defaultHeight
  //       ? breakpoints[2].defaultHeight
  //       :
  //   } else {

  //   }
  // }

  return figureSize;
};

const getRandomInt = max => {
  return Math.floor(Math.random() * max);
};

/**
 * Elements
 */
const container = select('#scrolly_1');
const windowSize = getWindowSize();
view = windowSize.width > 800 ? 'desktop' : 'mobile';

// figure
const figure = container.append('figure');
const figureSize = getFigureSize(windowSize);

const progressBar = figure.append('div').classed('progress-bar', true);
const stepsCount = 6;
const progressBubbles = progressBar
  .append('div')
  .classed('progress-bubbles', true)
  .selectAll('div.progress-bubble')
  .data([...Array(stepsCount).keys()])
  .join('div')
  .classed('progress-bubble', true)
  .attr('id', d => `progress-bubble__${d + 1}`)
  .attr('data-index', d => d);

const canvasContainer = figure.append('div').classed('canvas-container', true);

const canvas = canvasContainer.append('canvas');

const canvasOverlay = canvasContainer
  .append('div')
  .classed('canvas-overlay', true);

const maxAnnotationsCount = 5;
const annotations = canvasOverlay
  .append('div')
  .classed('annotation-container', true)
  .selectAll('div.annotation')
  .data([...Array(maxAnnotationsCount).keys()])
  .join('div')
  .classed('annotation', true)
  .attr('id', d => `annotation_${d + 1}`);

annotations.append('div').classed('label', true);

annotations.append('div').classed('number', true);

// article
const article = container.append('article');
const outerSteps = article
  .selectAll('div.step__outer')
  .data(text)
  .join('div')
  .classed('step__outer', true);
const innerSteps = outerSteps
  .append('div')
  .classed('step__inner', true)
  .attr('data-step-index', (d, i) => i);
innerSteps
  .append('p')
  .classed('t-copy', true)
  .text(d => d);
select('[data-step-index="4"]')
  .append('div')
  .classed('mini-results', true);

const resizeElements = () => {
  // update sizes
  const windowSize = getWindowSize();
  const figureSize = getFigureSize(windowSize);

  canvas.attr('width', figureSize.width).attr('height', figureSize.height);

  canvasContainer
    .style('width', `${figureSize.width}px`)
    .style('height', `${figureSize.height}px`);

  canvasOverlay
    .style('width', `${figureSize.width}px`)
    .style('height', `${figureSize.height}px`);

  const newView = windowSize.width > 800 ? 'desktop' : 'mobile';
  if (view != newView) {
    view = newView;
    initFishGraphic();
  } else {
    camera = new PerspectiveCamera(75, figureSize.width / figureSize.height);
    camera.position.set(0, 0, params[view].cameraPosition);
    camera.lookAt(0, 0, 0);
    scene.add(camera);
    renderer.setSize(figureSize.width, figureSize.height);
  }
};

/** Mini results graphics */
const miniResultsDataNested = nest()
  .key(d => d.party)
  .entries(miniResultsData);
miniResultsDataNested.reverse();

const resultsTable = select('.mini-results')
  .selectAll('div.mini-results__table')
  .data(miniResultsDataNested)
  .join('div')
  .classed('mini-results__table', true)
  .attr('id', d => `mini-results__table__${d.key}`);

resultsTable
  .append('div')
  .classed('party-name', true)
  .text(d => d.key);

resultsTable
  .append('div')
  .classed('table-headers', true)
  .selectAll('div.table-header')
  .data(['candidates', 'votes'])
  .join('div')
  .attr('class', 'table-header table-header__text')
  .text(d => d);

const tableRow = resultsTable
  .selectAll('div.table-row')
  .data(d => d.values)
  .join('div')
  .classed('table-row', true)
  .attr('id', d => d.candidate_slug);

tableRow
  .append('div')
  .classed('candidate__cell candidate__party', true)
  .append('span')
  .attr(
    'class',
    d =>
      d.party === 'Republican'
        ? 'circle-pill pill--rep'
        : 'circle-pill pill--dem',
    true
  )
  .text(d => (d.party === 'Republican' ? 'R' : 'D'));
tableRow
  .append('div')
  .classed('candidate__cell candidate__name', true)
  .each(function() {
    const data = select(this).data()[0];
    const name =
      data.is_winner === 'Yes' ? `✓ ${data.candidate}` : data.candidate;

    select(this)
      .append('span')
      .classed('t-weight-bold', true)
      .text(name);

    if (data.is_incumbent === 'Yes') {
      select(this)
        .append('span')
        .attr('class', 'pill pill--basic')
        .attr('title', 'Currently holds this seat')
        .text('Incumbent');
    }
  });
tableRow
  .append('div')
  .classed('candidate__cell candidate__vote', true)
  .text(d => d.votes);

const tableRowExtra = select('#mini-results__table__Democrat')
  .append('div')
  .attr('class', 'table-row table-row__extra');
tableRowExtra
  .append('div')
  .classed('table-header__text', true)
  .text('total');
tableRowExtra
  .append('div')
  .classed('candidate__vote', true)
  .text('8,377');

/**
 * Fish graphics
 */

let scene, camera, renderer;

const initFishGraphic = () => {
  // scene
  scene = new Scene();
  scene.background = new Color(colors.backgroudNavy);

  // camera
  camera = new PerspectiveCamera(75, figureSize.width / figureSize.height);
  camera.position.set(0, 0, params[view].cameraPosition);
  camera.lookAt(0, 0, 0);
  scene.add(camera);

  // mesh
  const geometries = [
    new ShapeGeometry(createFishShape1(new Shape())),
    new ShapeGeometry(createFishShape2(new Shape())),
    new ShapeGeometry(createFishShape3(new Shape())),
    new ShapeGeometry(createFishShape4(new Shape())),
  ];

  const texture = new TextureLoader().load('assets/texture/white.png');
  texture.colorSpace = SRGBColorSpace;
  texture.wrapS = texture.wrapT = RepeatWrapping;
  texture.repeat.set(0.008, 0.008);

  positionsData.forEach(p => {
    const geometry = geometries[getRandomInt(4)];
    const material = new MeshBasicMaterial({
      map: texture,
      color: colors.white,
    });

    const mesh = new Mesh(geometry, material);
    const data = scrollyData.filter(d => d.index === p.index);
    let defaultPosition;

    if (figureSize.breakpoint <= 370) {
      defaultPosition = new Vector3(
        (p.x - 2) * params[view].meshMarginScale.x - 80,
        -p.y * params[view].meshMarginScale.y,
        0
      );
      mesh.position.set(
        (p.x - 2) * params[view].meshMarginScale.x - 80,
        -p.y * params[view].meshMarginScale.y,
        0
      );
    } else if (figureSize.breakpoint <= 664) {
      defaultPosition = new Vector3(
        (p.x - 2) * params[view].meshMarginScale.x,
        -p.y * params[view].meshMarginScale.y,
        0
      );
      mesh.position.set(
        (p.x - 2) * params[view].meshMarginScale.x,
        -p.y * params[view].meshMarginScale.y,
        0
      );
    } else {
      defaultPosition = new Vector3(
        (p.x - 2) * params[view].meshMarginScale.x + 30,
        -p.y * params[view].meshMarginScale.y,
        0
      );
      mesh.position.set(
        (p.x - 2) * params[view].meshMarginScale.x + 30,
        -p.y * params[view].meshMarginScale.y,
        0
      );
    }

    mesh.index = p.index;
    mesh.userData = data;
    mesh.defaultPosition = defaultPosition;
    mesh.rotation.z = (Math.random() - 0.5) / params[view].meshRotationScale;
    mesh.scale.set(
      params[view].meshScale,
      params[view].meshScale,
      params[view].meshScale
    );

    scene.add(mesh);
  });

  // render
  renderer = new WebGLRenderer({
    canvas: canvas.node(),
    antialias: true,
  });
  renderer.setSize(figureSize.width, figureSize.height);
};

/**
 * Animation
 */
const clock = new Clock();

const animateFishGraphic = () => {
  const elapsedTime = clock.getElapsedTime();
  const meshes = scene.children.filter(el => el.type == 'Mesh');
  meshes.forEach((mesh, i) => {
    if (view == 'desktop') {
      mesh.position.y =
        mesh.defaultPosition.y +
        Math.sin(elapsedTime + mesh.defaultPosition.y + i) / 2;
    } else {
      mesh.position.x =
        mesh.defaultPosition.x +
        Math.sin(elapsedTime + mesh.defaultPosition.x + i);
    }
    mesh.position.z = Math.sin(elapsedTime + i) * 2;
  });
  renderer.render(scene, camera);

  requestAnimationFrame(animateFishGraphic);
};

/**
 * Scroller Functions
 */
const handleStepEnter = response => {
  const { element, direction, index } = response;

  const meshes = scene.children.filter(el => el.type == 'Mesh');

  // annotations
  const annotations = annotationsData.filter(
    d =>
      d.scrolly == scrolly &&
      d.width == figureSize.breakpoint &&
      d.step == index + 1
  );

  selectAll('.annotation')
    .select('.label')
    .text('');

  selectAll('.annotation')
    .select('number')
    .text('');

  select('.annotation-container').style('opacity', 0);

  let repeatBoolean = false;

  if (direction == 'down' && index == 4) {
    repeatBoolean = true;
  }
  if (direction == 'up' && index == 3) {
    repeatBoolean = true;
  }

  annotations.forEach(a => {
    const id = a.annotationId;
    const sel = select(`#annotation_${id}`);
    sel.style('top', `${a.top}px`).style('left', `${a.left}px`);
    sel.select('.label').html(`${a.text}`);
    sel.select('.number').html(`${a.number}`);
  });

  if (!repeatBoolean) {
    select('.annotation-container')
      .transition()
      .duration(1000)
      .style('opacity', 1);
  } else {
    select('.annotation-container').style('opacity', 1);
  }

  meshes.forEach((mesh, i) => {
    // move the fish
    const data = mesh.userData.find(
      d => d.scrolly === scrolly && d.view === view && d.step === index + 1
    );
    const defaultPosition = mesh.defaultPosition;
    const positionUnitToMove = data.position;

    const colorToChange = data.color;
    mesh.material.color = new Color(colors[colorToChange]);

    gsap.to(mesh.rotation, {
      z: (Math.random() - 0.5) / params[view].meshRotationScale,
      duration: params[view].meshRotationDuration,
    });

    if (view == 'desktop') {
      gsap.to(mesh.position, {
        x:
          defaultPosition.x +
          positionUnitToMove * params[view].meshTranslateScale.x,
        delay: i * params[view].meshStaggerScale,
      });
    } else {
      gsap.to(mesh.position, {
        y:
          defaultPosition.y +
          positionUnitToMove * params[view].meshTranslateScale.y,
        delay: i * params[view].meshStaggerScale,
      });
    }
  });
};

const handleStepProgress = response => {
  console.log('progress: ', response);

  progressBubbles
    .style('background-color', colors.backgroudNavy)
    .each(function() {
      const index = select(this).attr('data-index');
      const id = select(this).attr('id');
      if (index === response.index) {
        select(this)
          .style('background-color', colors.white)
          .style(
            'top',
            `${windowSize.height * 0.8 * -(response.progress - 1)}px`
          );
      }
    });

  const progressBubble = select(`#progress-bubble__${response.index + 1}`);
  // .attr('data-top', d => d * 6 * 2)
  // .attr('data-bottom', d => (-(d - 5) * 6 * 2))
  const top = progressBubble.attr('data-top');
  const bottom = progressBubble.attr('data-bottom');

  progressBubble.style('background-color', colors.white);
};

/**
 * Resize
 */
const handleResize = () => {
  resizeElements();
  scroller.resize();
  animateFishGraphic();
};

addEventListener('resize', handleResize);

const initScroller = () => {
  handleResize();
  scroller
    .setup({
      step: '#scrolly_1 article .step__outer',
      offset: 0.75,
      progress: true,
      threshold: 100,
    })
    .onStepEnter(handleStepEnter);
  // .onStepProgress(handleStepProgress);
};

/**
 * Instantiate
 */
initFishGraphic();
animateFishGraphic();
initScroller();
